import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { addNewVariableSchema } from "../../../schemas";

interface AvailableVariablesProps {
  error: any;
  setErrors: any;
  handleVariableSelect: any;
  isFlex?: boolean;
}

const AvailableVariables = ({
  error,
  setErrors,
  handleVariableSelect,
  isFlex,
}: AvailableVariablesProps) => {
  /** Controls the collapsible Add/Edit form */
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [newVariable, setNewVariable] = useState({
    var_name: "",
    var_value: "",
  });
  /**
   * null  → we are in "Add new variable" mode
   * string → we are editing the variable whose key equals this string
   */
  const [editingOriginalKey, setEditingOriginalKey] = useState<string | null>(
    null,
  );

  const { globalVariables, setGlobalVariables } = useRuleResourceContext();

  // ── Validation ────────────────────────────────────────────────────────────
  const validateField = async (name: any, value: any) => {
    try {
      const partialFormData = { ...newVariable, [name]: value };
      await addNewVariableSchema.validateAt(name, partialFormData);
      setErrors((prev: any) => ({ ...prev, [name]: undefined }));
    } catch (err: any) {
      setErrors((prev: any) => ({ ...prev, [name]: err.message }));
    }
  };

  const handleFieldChange = (name: string, value: any) => {
    setNewVariable((prev) => ({ ...prev, [name.toLowerCase()]: value }));
  };

  // ── Save (handles both Add and Edit modes) ────────────────────────────────
  const saveVariable = async () => {
    try {
      await addNewVariableSchema.validate(newVariable, { abortEarly: false });

      setGlobalVariables((prev: any) => {
        const updated = { ...prev };
        // If the key was renamed during edit (guard; field is disabled in edit mode)
        if (
          editingOriginalKey &&
          editingOriginalKey !== newVariable.var_name.toLowerCase()
        ) {
          delete updated[editingOriginalKey];
        }
        updated[newVariable.var_name.toLowerCase()] = newVariable.var_value;
        return updated;
      });

      closeForm();
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      if (validationErrors?.inner) {
        validationErrors.inner.forEach(
          (err: { path: string | number; message: string }) => {
            newErrors[err.path] = err.message;
          },
        );
      }
      setErrors((prev: any) => ({ ...prev, ...newErrors }));
    }
  };

  // ── Open / close helpers ──────────────────────────────────────────────────
  const openAddForm = () => {
    setEditingOriginalKey(null);
    setNewVariable({ var_name: "", var_value: "" });
    setErrors((prev: any) => ({
      ...prev,
      var_name: undefined,
      var_value: undefined,
    }));
    setIsFormOpen(true);
  };

  /**
   * Issue 2 fix: clicking the edit icon opens the collapsible form
   * pre-filled with the variable's current name (disabled) and value.
   */
  const openEditForm = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingOriginalKey(key);
    setNewVariable({
      var_name: key,
      var_value: String(globalVariables[key] ?? ""),
    });
    setErrors((prev: any) => ({
      ...prev,
      var_name: undefined,
      var_value: undefined,
    }));
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingOriginalKey(null);
    setNewVariable({ var_name: "", var_value: "" });
    setErrors((prev: any) => ({
      ...prev,
      var_name: undefined,
      var_value: undefined,
    }));
  };

  return (
    <>
      <Box className={`${isFlex ? "flex-props" : ""}`}>
        {/* ── Section header ──────────────────────────────────────────────── */}
        <Box className="var-chips-section-header">
          <h3>Available Variables:</h3>
          {/* Issue 4 fix: use btn-orange which maps to project's primary green */}
          {!isFormOpen && (
            <Button
              variant="contained"
              color="secondary"
              className="btn-orange btn-sm plus-btn-sm"
              onClick={openAddForm}
            >
              <AddSharpIcon />
            </Button>
          )}
        </Box>

        {/* ── Variable chips ────────────────────────────────────────────── */}
        <div className="var-chips-container">
          {globalVariables &&
            Object.keys(globalVariables).map((key, index) => {
              if (key === "") return null;
              const chipValue = globalVariables[key];

              return (
                <div
                  key={index}
                  className="var-chip"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      handleVariableSelect(key);
                  }}
                >
                  {/*
                   * Issue 1 fix: show only the key name (no $$), tooltip text
                   *              also has no $$  wrapping.
                   * Issue 6 fix: tooltip wraps only the clickable label span,
                   *              NOT the edit icon, so the two tooltips never
                   *              stack on top of each other.
                   */}
                  <Tooltip
                    title={`Click to insert "${key}" into query`}
                    placement="top"
                    arrow
                  >
                    <span
                      className="var-chip-label"
                      onClick={() => handleVariableSelect(key)}
                    >
                      <span className="var-chip-name">{key}</span>
                      {chipValue !== undefined && chipValue !== "" && (
                        <>
                          <span className="var-chip-separator">:</span>
                          <span className="var-chip-value">
                            {String(chipValue)}
                          </span>
                        </>
                      )}
                    </span>
                  </Tooltip>

                  {/*
                   * Issue 2 fix: clicking the pencil opens the form (pre-filled)
                   *              instead of showing an inline input inside the chip.
                   * Issue 3 fix: icon-btn-edit class aligns with existing edit icons.
                   * Issue 6 fix: this Tooltip is on its own element — no nesting.
                   */}
                  <Tooltip title="Edit value" placement="top" arrow>
                    <IconButton
                      size="small"
                      className="var-chip-edit-icon icon-btn-edit"
                      onClick={(e) => openEditForm(key, e)}
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              );
            })}
        </div>
      </Box>

      {/* ── Add / Edit Variable Form (collapsible with animation) ─────────── */}
      <Collapse
        in={isFormOpen}
        style={{ transformOrigin: "0 0 0 0" }}
        timeout={400}
      >
        <Box className="var-add-form-box">
          <Grid
            container
            rowSpacing={1.5}
            columnSpacing={2}
            alignItems="flex-start"
          >
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <label className="label-text">
                Variable name <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="var_name"
                value={newVariable.var_name}
                /**
                 * Disabled in edit mode — the name identifies which variable
                 * is being updated; only the value should change.
                 */
                disabled={!!editingOriginalKey}
                onChange={(event) => {
                  handleFieldChange(event.target.name, event.target.value);
                  validateField(event.target.name, event.target.value);
                }}
                fullWidth
                variant="outlined"
                size="small"
                className={`form-control-autocomplete editor-textbox ${
                  error?.var_name ? "has-error" : ""
                }`}
                error={!!error?.var_name}
                helperText={error?.var_name}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6}>
              <label className="label-text">
                Variable value <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="var_value"
                value={newVariable.var_value}
                onChange={(event) => {
                  handleFieldChange(event.target.name, event.target.value);
                  validateField(event.target.name, event.target.value);
                }}
                fullWidth
                variant="outlined"
                size="small"
                className={`form-control-autocomplete editor-textbox ${
                  error?.var_value ? "has-error" : ""
                }`}
                error={!!error?.var_value}
                helperText={error?.var_value}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12}>
              {/* Spacer reserving the label row so the buttons line up with the
                  inputs. Only rendered at lg+, where the buttons share a row
                  with the fields; below that they occupy their own row. */}
              <Box
                sx={{ display: { md: "block", sm: "none", xs: "none" } }}
                className="label-text"
              >
                &nbsp;
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  columnGap: "8px",
                  justifyContent: "flex-end",
                  flexWrap: "nowrap",
                }}
              >
                <Button
                  color="secondary"
                  variant="contained"
                  className="btn-orange"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={saveVariable}
                >
                  <SaveOutlinedIcon fontSize="small" /> &nbsp;Save
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={closeForm}
                  className="btn-orange btn-dark"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </>
  );
};

export default AvailableVariables;
